import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const services = [
  {
    tag: '01',
    title: 'Merchant Cash Advance',
    range: '$25K — $2M',
    time: '24–48 hours',
    desc: 'Fast, flexible capital advanced against future receivables. Approval based on revenue, not credit score. Repayment scales with your sales.',
  },
  {
    tag: '02',
    title: 'Equipment Financing',
    range: '$50K — $10M',
    time: '3–5 days',
    desc: 'Asset-backed financing for machinery, vehicles, and infrastructure. Terms up to 84 months with competitive fixed rates and 100% financing options.',
  },
  {
    tag: '03',
    title: 'SBA Loans',
    range: '$150K — $5M',
    time: '30–45 days',
    desc: 'Government-guaranteed 7(a) and 504 programs for acquisition, expansion, and refinance. Long amortization, low rates, and flexible use of proceeds.',
  },
  {
    tag: '04',
    title: 'HELOC',
    range: '$50K — $500K',
    time: '10–14 days',
    desc: 'Home Equity Line of Credit for business owners. Draw only what you need, pay interest only on the balance used. Rates from prime + 0%.',
  },
  {
    tag: '05',
    title: 'Invoice Factoring',
    range: '$25K — $10M',
    time: '24–72 hours',
    desc: 'Convert outstanding B2B invoices into immediate working capital. Non-recourse options available. Advance rates up to 90% of invoice face value.',
  },
  {
    tag: '06',
    title: 'Business Line of Credit',
    range: '$25K — $1M',
    time: '24 hours',
    desc: 'Revolving access to capital when you need it. Draw, repay, redraw. Only pay for what you use, single underwriting event, unsecured options available.',
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative bg-[#050505] py-28 lg:py-40"
      data-testid="services-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-20 lg:mb-28">
          <div className="lg:col-span-4 flex items-start gap-4">
            <span className="w-10 h-px bg-[#c2a978] mt-3" />
            <span className="text-[10px] tracking-[0.5em] uppercase text-[#c2a978]">
              Ch. 01 — Capital
            </span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-8 font-serif text-3xl lg:text-5xl leading-[0.95] tracking-[-0.02em] text-[#f5f5f0]"
          >
            Six programs.<br />
            One <span className="italic text-[#c2a978]">unfair</span> advantage.
          </motion.h2>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#c2a978]/10">
          {services.map((s, i) => (
            <motion.a
              href="https://app.scorpionfunding.com" target="_blank" rel="noopener noreferrer"
              key={s.tag}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: 'easeOut' }}
              className="group relative bg-[#050505] p-8 lg:p-10 min-h-[320px] flex flex-col justify-between hover:bg-[#0a0a0a] transition-colors duration-500"
              data-testid={`service-card-${i}`}
            >
              <div>
                <div className="flex items-start justify-between mb-10">
                  <span className="font-serif italic text-[#c2a978] text-3xl">
                    {s.tag}
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="text-[#8a8a8a] group-hover:text-[#c2a978] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                  />
                </div>
                <h3 className="font-serif text-2xl lg:text-3xl text-[#f5f5f0] mb-4 leading-tight">
                  {s.title}
                </h3>
                <p className="text-[#8a8a8a] text-sm leading-relaxed max-w-sm">
                  {s.desc}
                </p>
              </div>
              <div className="mt-8 pt-6 border-t border-[#c2a978]/10 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase">
                <span className="text-[#c2a978]">{s.range}</span>
                <span className="text-[#8a8a8a]">{s.time}</span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
